/**
 * Modal de Configuración del Formulario de Evento.
 *
 * Este componente proporciona una interfaz modal para editar la configuración
 * general de los formularios de registro de eventos, incluyendo fechas de
 * disponibilidad del formulario, vigencia de carnets y límites de registro.
 *
 * @author Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-11-12]
 * @updated [Fecha de última modificación: 2025-11-12]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import React from "react";
import {
  Modal,
  Card,
  Typography,
  Icon,
  Divider,
  Box,
  TextField,
  Grid,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ToolTip from "components/ToolTip";

/**
 * Componente modal para editar la configuración del formulario de evento.
 *
 * Este componente renderiza un modal con formulario que permite configurar
 * las fechas de disponibilidad del formulario, vigencia de carnets generados,
 * límite máximo de registros y estado activo/inactivo del formulario.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.open - Controla si el modal está abierto.
 * @param {Function} props.onClose - Función que se ejecuta al cerrar el modal.
 * @param {Object} props.editedData - Objeto con los datos actuales del formulario.
 * @param {string} props.editedData.form_start_date - Fecha inicio del formulario (YYYY-MM-DDTHH:MM).
 * @param {string} props.editedData.form_end_date - Fecha cierre del formulario (YYYY-MM-DDTHH:MM).
 * @param {string} props.editedData.card_effective_date - Fecha activación del carnet (YYYY-MM-DDTHH:MM).
 * @param {string} props.editedData.card_expiration_date - Fecha expiración del carnet (YYYY-MM-DDTHH:MM).
 * @param {boolean} props.editedData.is_active - Estado activo/inactivo del formulario.
 * @param {number|string} props.editedData.max_registrations - Límite máximo de registros.
 * @param {Function} props.handleFieldChange - Función para actualizar valores del formulario.
 * @param {Function} props.onSave - Función que se ejecuta al guardar la configuración.
 * @param {boolean} props.saving - Indica si se está guardando actualmente.
 * @returns {JSX.Element} Modal con formulario de configuración del evento.
 */
export default function EventConfigModal({
  open,
  onClose,
  editedData,
  handleFieldChange,
  onSave,
  saving,
}) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "800px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-config-title"
      aria-describedby="modal-config-description"
    >
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <MDTypography variant="h5" color="white" fontWeight="medium">
            Editar Configuración del Formulario
          </MDTypography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              marginLeft: "auto",
            }}
            onClick={onClose}
          >
            close
          </Icon>
        </MDBox>
        <Divider sx={{ my: 0 }} />

        <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <MDBox pt={4} pb={3} px={3}>
            {/* Fechas del Formulario */}
            <MDBox mb={4}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#00416A" }}>
                  Fechas del Formulario de Registro
                </MDTypography>
                <ToolTip message="Define el período durante el cual el formulario estará disponible para que los usuarios puedan registrarse" />
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    Fecha de inicio
                  </Typography>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    value={editedData.form_start_date}
                    onChange={(e) => handleFieldChange("form_start_date", e.target.value)}
                    disabled={saving}
                    InputLabelProps={{ shrink: true }}
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    Fecha de cierre
                  </Typography>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    value={editedData.form_end_date}
                    onChange={(e) => handleFieldChange("form_end_date", e.target.value)}
                    disabled={saving}
                    InputLabelProps={{ shrink: true }}
                    size="medium"
                  />
                </Grid>
              </Grid>
            </MDBox>

            {/* Vigencia del Carnet */}
            <MDBox mb={4}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#00416A" }}>
                  Vigencia del Carnet
                </MDTypography>
                <ToolTip message="Define el período de validez del carnet que se generará para las personas registradas" />
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    Fecha de activación
                  </Typography>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    value={editedData.card_effective_date}
                    onChange={(e) => handleFieldChange("card_effective_date", e.target.value)}
                    disabled={saving}
                    InputLabelProps={{ shrink: true }}
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    Fecha de expiración
                  </Typography>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    value={editedData.card_expiration_date}
                    onChange={(e) => handleFieldChange("card_expiration_date", e.target.value)}
                    disabled={saving}
                    InputLabelProps={{ shrink: true }}
                    size="medium"
                  />
                </Grid>
              </Grid>
            </MDBox>

            {/* Configuración General */}
            <MDBox mb={4}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#00416A" }}>
                  Configuración General
                </MDTypography>
                <ToolTip message="Parámetros adicionales para controlar el comportamiento del formulario" />
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    Máximo de registros
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={editedData.max_registrations}
                    onChange={(e) => handleFieldChange("max_registrations", e.target.value)}
                    disabled={saving}
                    InputProps={{ inputProps: { min: 0 } }}
                    size="medium"
                    placeholder="vacío para ilimitado"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    Estado del formulario
                  </Typography>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="status-radio-group"
                      name="status-radio-group"
                      value={editedData.is_active ? 1 : 0}
                      onChange={(e) => handleFieldChange("is_active", e.target.value === "1")}
                    >
                      <FormControlLabel
                        disabled={saving}
                        value={1}
                        control={<Radio />}
                        label="Activo"
                      />
                      <FormControlLabel
                        disabled={saving}
                        value={0}
                        control={<Radio />}
                        label="Inactivo"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    Diseño del formulario
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={editedData.template || "default"}
                    onChange={(e) => handleFieldChange("template", e.target.value)}
                    disabled={saving}
                    size="medium"
                  >
                    <MenuItem value="default">Predeterminado</MenuItem>
                    <MenuItem value="custom_1">Personalizado 1</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </MDBox>

            <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
              <MDButton onClick={onClose} color="error" disabled={saving}>
                Cancelar
              </MDButton>
              <MDButton onClick={onSave} color="success" disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </MDButton>
            </Box>
          </MDBox>
        </div>
      </Card>
    </Modal>
  );
}
